/**
 * MMM Configuration Form Validation Utilities
 * Provides validation functions for all configuration fields
 */

import {
  MMMConfigFormState,
  ValidationError,
  MediaChannel,
  ControlVar,
  LiftTest,
} from "../types/mmmConfig";

// === Core Validation Functions ===

/**
 * Check if a value is required and not empty
 */
export const isRequired = (value: any): boolean => {
  if (value === null || value === undefined) return false;
  if (typeof value === "string") return value.trim().length > 0;
  if (Array.isArray(value)) return value.length > 0;
  if (typeof value === "object") return Object.keys(value).length > 0;
  return true;
};

/**
 * Check if a string matches a pattern
 */
export const matchesPattern = (value: string, pattern: RegExp): boolean => {
  return pattern.test(value);
};

/**
 * Check if a column exists in list
 */
export const columnExists = (column: string, columns: string[]): boolean => {
  return columns.includes(column);
};

/**
 * Check if all items in array are unique
 */
export const isUnique = (items: string[]): boolean => {
  return items.length === new Set(items).size;
};

/**
 * Check if a value is within a range
 */
export const inRange = (value: number, min?: number, max?: number): boolean => {
  if (min !== undefined && value < min) return false;
  if (max !== undefined && value > max) return false;
  return true;
};

// === Field-Level Validators ===

export const validateDepVar = (
  depVar: string,
  dataColumns: string[]
): ValidationError | null => {
  if (!isRequired(depVar)) {
    return {
      field: "dep_var",
      message: "Dependent variable is required",
      type: "required",
    };
  }
  if (!columnExists(depVar, dataColumns)) {
    return {
      field: "dep_var",
      message: `Column "${depVar}" not found in data`,
      type: "custom",
    };
  }
  return null;
};

export const validateDateVar = (
  dateVar: string,
  dataColumns: string[]
): ValidationError | null => {
  if (!isRequired(dateVar)) {
    return {
      field: "date_var",
      message: "Date column is required",
      type: "required",
    };
  }
  if (!columnExists(dateVar, dataColumns)) {
    return {
      field: "date_var",
      message: `Column "${dateVar}" not found in data`,
      type: "custom",
    };
  }
  return null;
};

export const validateMediaChannels = (
  channels: MediaChannel[],
  dataColumns: string[]
): ValidationError[] => {
  const errors: ValidationError[] = [];

  // At least one channel required
  if (!isRequired(channels)) {
    errors.push({
      field: "media_channels",
      message: "At least one media channel is required",
      type: "required",
    });
    return errors;
  }

  // Check channel names
  const names = channels.map((ch) => ch.name);
  if (!isUnique(names)) {
    errors.push({
      field: "media_channels",
      message: "Channel names must be unique",
      type: "custom",
    });
  }

  // Validate each channel
  channels.forEach((channel, index) => {
    if (!isRequired(channel.name)) {
      errors.push({
        field: `media_channels.${index}.name`,
        message: "Channel name is required",
        type: "required",
      });
    }
    if (!matchesPattern(channel.name, /^[a-zA-Z0-9_]+$/)) {
      errors.push({
        field: `media_channels.${index}.name`,
        message: "Channel name must contain only letters, numbers, and underscores",
        type: "custom",
      });
    }
    if (!isRequired(channel.column)) {
      errors.push({
        field: `media_channels.${index}.column`,
        message: "Data column is required",
        type: "required",
      });
    }
    if (!columnExists(channel.column, dataColumns)) {
      errors.push({
        field: `media_channels.${index}.column`,
        message: `Column "${channel.column}" not found in data`,
        type: "custom",
      });
    }
  });

  return errors;
};

export const validateControlVars = (
  vars: ControlVar[],
  dataColumns: string[]
): ValidationError[] => {
  const errors: ValidationError[] = [];

  if (!Array.isArray(vars)) return errors; // Optional

  // Check var names
  const names = vars.map((v) => v.name);
  if (names.length !== new Set(names).size) {
    errors.push({
      field: "control_vars",
      message: "Control variable names must be unique",
      type: "custom",
    });
  }

  // Validate each var
  vars.forEach((control, index) => {
    if (!isRequired(control.name)) {
      errors.push({
        field: `control_vars.${index}.name`,
        message: "Variable name is required",
        type: "required",
      });
    }
    if (!matchesPattern(control.name, /^[a-zA-Z0-9_]+$/)) {
      errors.push({
        field: `control_vars.${index}.name`,
        message: "Variable name must contain only letters, numbers, and underscores",
        type: "custom",
      });
    }
    if (!isRequired(control.column)) {
      errors.push({
        field: `control_vars.${index}.column`,
        message: "Data column is required",
        type: "required",
      });
    }
    if (!columnExists(control.column, dataColumns)) {
      errors.push({
        field: `control_vars.${index}.column`,
        message: `Column "${control.column}" not found in data`,
        type: "custom",
      });
    }
  });

  return errors;
};

export const validateLagPeriods = (periods: number[]): ValidationError | null => {
  if (!Array.isArray(periods) || periods.length === 0) {
    return {
      field: "feature_engineering.lag_periods",
      message: "At least one lag period is required",
      type: "required",
    };
  }

  for (const period of periods) {
    if (!Number.isInteger(period) || period <= 0) {
      return {
        field: "feature_engineering.lag_periods",
        message: "All lag periods must be positive integers",
        type: "type",
      };
    }
  }

  return null;
};

export const validateTrainTestSplit = (
  trainRatio: number,
  valRatio?: number | null,
  testRatio?: number | null
): ValidationError | null => {
  // Validate train ratio
  if (!inRange(trainRatio, 0.05, 0.95)) {
    return {
      field: "train_test_split.train_ratio",
      message: "Train ratio must be between 0.05 and 0.95",
      type: "range",
    };
  }

  // If 3-way split, validate ratios sum to 1.0
  if (valRatio != null && testRatio != null) {
    const sum = trainRatio + valRatio + testRatio;
    if (Math.abs(sum - 1.0) > 0.01) {
      return {
        field: "train_test_split",
        message: `Ratios must sum to 1.0 (got ${sum.toFixed(3)})`,
        type: "custom",
      };
    }

    if (!inRange(valRatio, 0.01, 0.4)) {
      return {
        field: "train_test_split.val_ratio",
        message: "Validation ratio must be between 0.01 and 0.4",
        type: "range",
      };
    }

    if (!inRange(testRatio, 0.01, 0.4)) {
      return {
        field: "train_test_split.test_ratio",
        message: "Test ratio must be between 0.01 and 0.4",
        type: "range",
      };
    }
  }

  return null;
};

export const validateRidgeAlpha = (
  lower: number,
  upper: number
): ValidationError | null => {
  if (lower <= 0) {
    return {
      field: "hyperparams.ridge_alpha.lower",
      message: "Lower bound must be > 0",
      type: "range",
    };
  }
  if (upper <= 0) {
    return {
      field: "hyperparams.ridge_alpha.upper",
      message: "Upper bound must be > 0",
      type: "range",
    };
  }
  if (lower >= upper) {
    return {
      field: "hyperparams.ridge_alpha",
      message: "Upper bound must be greater than lower bound",
      type: "custom",
    };
  }
  return null;
};

export const validateBudget = (budget: number): ValidationError | null => {
  if (!Number.isInteger(budget)) {
    return {
      field: "optimization.budget",
      message: "Budget must be an integer",
      type: "type",
    };
  }
  if (budget < 50 || budget > 10000) {
    return {
      field: "optimization.budget",
      message: "Budget must be between 50 and 10000",
      type: "range",
    };
  }
  return null;
};

export const validateObjectiveWeights = (
  nrmse: number,
  decompRssd: number,
  mapeLift: number
): ValidationError | null => {
  if (nrmse < 0 || decompRssd < 0 || mapeLift < 0) {
    return {
      field: "optimization.objective_weights",
      message: "All weights must be non-negative",
      type: "range",
    };
  }
  if (nrmse === 0 && decompRssd === 0 && mapeLift === 0) {
    return {
      field: "optimization.objective_weights",
      message: "At least one weight must be > 0",
      type: "custom",
    };
  }
  return null;
};

export const validateLiftTests = (
  liftTests: LiftTest[],
  mediaChannelNames: string[]
): ValidationError[] => {
  const errors: ValidationError[] = [];

  if (!Array.isArray(liftTests)) return errors;

  liftTests.forEach((test, index) => {
    if (!isRequired(test.channel)) {
      errors.push({
        field: `lift_tests.lift_tests.${index}.channel`,
        message: "Channel is required",
        type: "required",
      });
    } else if (!mediaChannelNames.includes(test.channel)) {
      errors.push({
        field: `lift_tests.lift_tests.${index}.channel`,
        message: `Channel "${test.channel}" not found in media channels`,
        type: "custom",
      });
    }

    if (typeof test.observed_lift !== "number" || test.observed_lift <= 0) {
      errors.push({
        field: `lift_tests.lift_tests.${index}.observed_lift`,
        message: "Observed lift must be a positive number",
        type: "type",
      });
    }

    if (test.confidence_interval && (test.confidence_interval < 0 || test.confidence_interval > 1)) {
      errors.push({
        field: `lift_tests.lift_tests.${index}.confidence_interval`,
        message: "Confidence interval must be between 0 and 1",
        type: "range",
      });
    }

    if (test.sample_size && (!Number.isInteger(test.sample_size) || test.sample_size <= 0)) {
      errors.push({
        field: `lift_tests.lift_tests.${index}.sample_size`,
        message: "Sample size must be a positive integer",
        type: "type",
      });
    }
  });

  return errors;
};

// === Full Form Validation ===

/**
 * Validate entire MMM configuration form state
 * Returns array of validation errors (empty if valid)
 */
export const validateMMMConfig = (
  config: MMMConfigFormState,
  dataColumns: string[]
): ValidationError[] => {
  const errors: ValidationError[] = [];

  // Global
  const depVarError = validateDepVar(config.dep_var, dataColumns);
  if (depVarError) errors.push(depVarError);

  const dateVarError = validateDateVar(config.date_var, dataColumns);
  if (dateVarError) errors.push(dateVarError);

  // Media channels
  errors.push(...validateMediaChannels(config.media_channels || [], dataColumns));

  // Control vars
  errors.push(...validateControlVars(config.control_vars || [], dataColumns));

  // Feature engineering
  if (config.feature_engineering?.enabled) {
    if (config.feature_engineering.use_lagged_features) {
      const lagError = validateLagPeriods(config.feature_engineering.lag_periods || []);
      if (lagError) errors.push(lagError);
    }
  }

  // Train/test split
  const splitError = validateTrainTestSplit(
    config.train_test_split?.train_ratio || 0.8,
    config.train_test_split?.val_ratio,
    config.train_test_split?.test_ratio
  );
  if (splitError) errors.push(splitError);

  // Hyperparameters
  const ridgeError = validateRidgeAlpha(
    config.hyperparams?.ridge_alpha?.lower || 0.0001,
    config.hyperparams?.ridge_alpha?.upper || 100
  );
  if (ridgeError) errors.push(ridgeError);

  // Optimization
  const budgetError = validateBudget(config.optimization?.budget || 2000);
  if (budgetError) errors.push(budgetError);

  const weightsError = validateObjectiveWeights(
    config.optimization?.objective_weights?.nrmse || 1.0,
    config.optimization?.objective_weights?.decomp_rssd || 1.0,
    config.optimization?.objective_weights?.mape_lift || 0.0
  );
  if (weightsError) errors.push(weightsError);

  // Lift tests
  if (config.lift_tests?.enabled) {
    const mediaChannelNames = config.media_channels.map((ch) => ch.name);
    errors.push(...validateLiftTests(config.lift_tests.lift_tests || [], mediaChannelNames));
  }

  return errors;
};

/**
 * Check if form is valid (no errors)
 */
export const isFormValid = (
  config: MMMConfigFormState,
  dataColumns: string[]
): boolean => {
  return validateMMMConfig(config, dataColumns).length === 0;
};

/**
 * Get error for a specific field
 */
export const getFieldError = (
  field: string,
  errors: ValidationError[]
): string | null => {
  const error = errors.find((e) => e.field === field);
  return error?.message || null;
};

/**
 * Check if a field has errors
 */
export const hasFieldError = (field: string, errors: ValidationError[]): boolean => {
  return errors.some((e) => e.field === field);
};

/**
 * Get all errors for a section (e.g., all media_channels errors)
 */
export const getSectionErrors = (
  section: string,
  errors: ValidationError[]
): ValidationError[] => {
  return errors.filter((e) => e.field.startsWith(section));
};

// === Conditional Validation (for real-time feedback) ===

/**
 * Validate a field as user types
 * Returns error message or null if valid
 */
export const validateField = (
  fieldName: string,
  value: any,
  config: MMMConfigFormState,
  dataColumns: string[]
): string | null => {
  switch (fieldName) {
    case "dep_var":
      return validateDepVar(value, dataColumns)?.message || null;

    case "date_var":
      return validateDateVar(value, dataColumns)?.message || null;

    case "train_ratio":
      return validateTrainTestSplit(value, config.train_test_split?.val_ratio)?.message ||
        null;

    case "val_ratio":
      return validateTrainTestSplit(
        config.train_test_split?.train_ratio || 0.8,
        value,
        config.train_test_split?.test_ratio
      )?.message || null;

    case "test_ratio":
      return validateTrainTestSplit(
        config.train_test_split?.train_ratio || 0.8,
        config.train_test_split?.val_ratio,
        value
      )?.message || null;

    case "ridge_alpha_lower":
    case "ridge_alpha_upper":
      return validateRidgeAlpha(
        fieldName === "ridge_alpha_lower" ? value : config.hyperparams.ridge_alpha.lower,
        fieldName === "ridge_alpha_upper" ? value : config.hyperparams.ridge_alpha.upper
      )?.message || null;

    case "budget":
      return validateBudget(value)?.message || null;

    default:
      return null;
  }
};

// === Formatting Helpers ===

/**
 * Format validation errors for display
 */
export const formatValidationErrors = (errors: ValidationError[]): string => {
  if (errors.length === 0) return "No errors";
  return errors.map((e) => `${e.field}: ${e.message}`).join("\n");
};

/**
 * Group errors by field for better display
 */
export const groupErrorsByField = (
  errors: ValidationError[]
): Record<string, string[]> => {
  const grouped: Record<string, string[]> = {};
  errors.forEach((error) => {
    if (!grouped[error.field]) grouped[error.field] = [];
    grouped[error.field].push(error.message);
  });
  return grouped;
};
